import * as React from 'react';

/**
 * Wraps each occurrence of `term` (case-insensitive) inside `text` in a
 * `<mark>` element. Returns the original string when there's no term
 * or no match — keeps the cost negligible for the common case.
 *
 * Uses semantic `<mark>` so screen readers announce highlights. The
 * caller styles `mark` via CSS in this file's neighbour stylesheets, or
 * the inline default below kicks in.
 *
 * Composes with `renderTextWithMentions` by accepting either a string
 * or pre-rendered React nodes — when given nodes we walk strings inside
 * Fragments and apply the same logic, leaving non-string children
 * untouched (already-styled mention chips, etc.).
 */
export function highlightTerm(
  text: string | undefined | null,
  term: string,
): React.ReactNode {
  if (!text) return text;
  if (!term.trim()) return text;
  const lcText = text.toLowerCase();
  const lcTerm = term.toLowerCase();
  if (!lcText.includes(lcTerm)) return text;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (;;) {
    const idx = lcText.indexOf(lcTerm, cursor);
    if (idx === -1) break;
    if (idx > cursor) {
      parts.push(
        <React.Fragment key={key++}>{text.slice(cursor, idx)}</React.Fragment>,
      );
    }
    parts.push(
      <mark
        key={key++}
        style={{
          background: 'rgba(255, 200, 0, 0.45)',
          color: 'inherit',
          padding: '0 1px',
          borderRadius: 2,
        }}
      >
        {text.slice(idx, idx + lcTerm.length)}
      </mark>,
    );
    cursor = idx + lcTerm.length;
  }
  if (cursor < text.length) {
    parts.push(
      <React.Fragment key={key}>{text.slice(cursor)}</React.Fragment>,
    );
  }
  return parts;
}
